import { memo, useEffect, useCallback, useState } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Button } from "@mui/material";
import { getOwners, updateScrapie, getScrapies } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import {
    registrationCodeHeaderNoClick,
    flockCodeHeader,
    sexHeaderNoFilter,
    birthDateHeaderNotFilterable,
    regDateHeader,
} from "../../components/TableHeaders.js";
import { dayjs } from "../../utils/index.js";

const UpdateScrapie = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [animalsDropdownValues, setAnimalsDropdownValues] = useState([]);
    const [step, setStep] = useState(1);
    const [fetchedScrapies, setFetchedScrapies] = useState([]);
    const [selectedScrapie, setSelectedScrapie] = useState(null);
    const [selectedAnimal, setSelectedAnimal] = useState(null);
    const [scrapieCds, setScrapieCds] = useState([]);
    const [scrapieCodesDropdownValues, setScrapieCodesDropdownValues] = useState([]);
    const [sampleStts, setSampleStts] = useState([]);
    const [sampleStatesDropdownValues, setSampleStatesDropdownValues] = useState([]);

    const selectClasses = useSelectStyles();
    const listClasses = useListStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, [error]);

    useEffect(() => {
        fetchData();
    }, [])

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const selectScrapieFormContent = [
        {
            customType: "dropdown",
            id: "animal",
            items: animalsDropdownValues,
            placeholder: "Επιλογή ζώου",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ]

    const scrapieFormContent = [
        {
            customType: "input",
            id: "sampleNumber",
            type: "text",
            placeholder: "Αριθμός Δείγματος",
            disabled: false,
            value: selectedScrapie?.sampleCode,
        },
        {
            customType: "dropdown",
            id: "sampleState",
            items: sampleStatesDropdownValues,
            placeholder: "Κατάσταση Δείγματος",
            required: false,
            defaultValue: sampleStts.find((state) => state.sampleStateCode === selectedScrapie?.sampleState)?.sampleStateName,
        },
        {
            customType: "dropdown",
            id: "firstResult",
            items: scrapieCodesDropdownValues,
            placeholder: "Πρώτο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds.find((code) => Number(code.scrapieCode) === selectedScrapie?.result1)?.scrapieName,
        },
        {
            customType: "dropdown",
            id: "secondResult",
            items: scrapieCodesDropdownValues,
            placeholder: "Δεύτερο Αποτέλεσμα",
            required: true,
            defaultValue: scrapieCds.find((code) => Number(code.scrapieCode) === selectedScrapie?.result2)?.scrapieName,
        },
        {
            customType: "date-picker",
            id: "testDate",
            type: "desktop",
            label: "Ηερομηνία Δειγματοληψίας",
            width: "595px",
            background: "third",
            required: true,
            value: dayjs(selectedScrapie?.sampleDate),
        },
        {
            customType: "date-picker",
            id: "resultDate",
            type: "desktop",
            label: "Ημερομηνία Αποτελεσμάτων",
            width: "595px",
            background: "third",
            required: true,
            value: dayjs(selectedScrapie?.resultDate),
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const tableColumns = [
        registrationCodeHeaderNoClick,
        flockCodeHeader,
        sexHeaderNoFilter,
        birthDateHeaderNotFilterable,
        regDateHeader,
    ];

    const handleOwnerSelect = async (values) => {
        setIsLoading(true);
        const selectedOwner = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selectedOwner);
        const { success: scs, scrapies, scrapieCodes, sampleStates, message } = await getScrapies(selectedOwner?._id);
        if (scs) {
            setFetchedScrapies(scrapies);
            setAnimalsDropdownValues(
                scrapies.map((scrapie) => ({
                    value: `${scrapie?.registrationCode}`,
                    text: `${scrapie?.registrationCode}`,
                }))
            );
            setScrapieCds(scrapieCodes);
            setScrapieCodesDropdownValues(scrapieCodes.map((code) => ({
                value: code.scrapieName,
                text: code.scrapieName,
            })));
            setSampleStatesDropdownValues(sampleStates.map((state) => ({
                value: state.sampleStateName,
                text: state.sampleStateName,
            })));
            setSampleStts(sampleStates);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const handleScrapieSelect = (values) => {
        const selectedScrapie = fetchedScrapies.find(
            (scrapie) => `${scrapie?.registrationCode}` === values.animal
        );
        setSelectedScrapie(selectedScrapie);
        setSelectedAnimal(selectedScrapie?.animalId);
        setStep(3);
    }

    const handleScrapieUpdate = async (values) => {
        setIsLoading(true);
        const scrapieId = selectedScrapie?._id;
        const ownerId = selectedOwner?._id;
        const animalId = selectedAnimal?._id;
        const sampleCode = values.sampleNumber;
        const sampleDate = values.testDate;
        const resultDate = values.resultDate;
        const sampleState = sampleStts.find((state) => state.sampleStateName === values.sampleState)?.sampleStateCode;
        const result1 = scrapieCds.find((code) => code.scrapieName === values.firstResult)?.scrapieCode;
        const result2 = scrapieCds.find((code) => code.scrapieName === values.secondResult)?.scrapieCode;
        const { success: scs, message } = await updateScrapie(
            scrapieId,
            ownerId,
            animalId,
            sampleCode,
            sampleDate,
            resultDate,
            sampleState,
            result1,
            result2,
        );
        if (scs) {
            success(message);
            const { success: scs, scrapies, scrapieCodes, sampleStates, msg } = await getScrapies(selectedOwner?._id);
            if (scs) {
                setFetchedScrapies(scrapies);
                setAnimalsDropdownValues(
                    scrapies.map((scrapie) => ({
                        value: `${scrapie?.registrationCode}`,
                        text: `${scrapie?.registrationCode}`,
                    }))
                );
                setScrapieCds(scrapieCodes);
                setScrapieCodesDropdownValues(scrapieCodes.map((code) => ({
                    value: code.scrapieName,
                    text: code.scrapieName,
                })));
                setSampleStatesDropdownValues(sampleStates.map((state) => ({
                    value: state.sampleStateName,
                    text: state.sampleStateName,
                })));
                setSampleStts(sampleStates);
                setStep(2);
            } else {
                error(msg);
            }
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }
    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή που σας ενδιαφέρει:
                        </Typography>
                        <Form
                            content={ownerFormContent}
                            onSubmit={handleOwnerSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε το Ζώο που σας ενδιαφέρει:
                        </Typography>
                        <Form
                            content={selectScrapieFormContent}
                            onSubmit={handleScrapieSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 3 && selectedScrapie && (
                <>
                    <Grid2
                        item
                        xs={12}
                        md={12}
                        sx={{
                            mt: "2rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="h6" className={listClasses.form_title} sx={{ display: "flex", justifyContent: "center" }}>
                            {"Στοιχεία Επιλεγμένου Ζώου"}
                        </Typography>
                        <Table
                            data={[selectedAnimal]}
                            noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχει έλεγχος"}
                            columns={tableColumns}
                            defaultSorted={[{ id: "registeredAt", desc: true }]}
                            showPageSizeOptions={false}
                        />
                    </Grid2>
                    <Box className={selectClasses.outerBox} sx={{ marginTop: "-5rem" }}>
                        <Box container flexDirection="column" className={selectClasses.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Ενημέρωση Στοιχείων SCARPIE"}
                            </Typography>
                            <Form
                                content={scrapieFormContent}
                                onSubmit={handleScrapieUpdate}
                                toResetForm={true}
                            />
                        </Box>
                    </Box>
                </>
            )}
            {step !== 1 && (
                <Box sx={{ width: "100%", marginTop: step === 3 ? "-8rem" : "-13rem", display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" color="secondary" onClick={() => setStep(step - 1)}>
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    )
}

export default memo(UpdateScrapie);
